import _ from 'lodash';

import { SnapCandidateType, type SnapCandidate } from './snapTo.types';

import type { StageRect } from '#packages/stateManagement';
import type { CompData, CompLayout } from 'types/documentServices';

type Axis = 'x' | 'y';

const getEqualDistanceCandidates = function (
  currentPageId: string,
  stageRect: StageRect,
  compLayout: CompLayout,
  snapCandidates: SnapCandidate[],
  axis: Axis,
) {
  const visibleCandidates = snapCandidates.filter(
    _.partial(isLayoutVisible, stageRect),
  );
  const equalDistanceCandidates = filterEqualDistanceCandidates(
    visibleCandidates,
    currentPageId,
  );
  if (axis === 'x') {
    const horizontalCenterCandidates = filterCenterCandidates(
      compLayout,
      equalDistanceCandidates,
      'y',
      'height',
    );
    if (horizontalCenterCandidates.length > 0) {
      return addAxisAlignmentCoordinate(
        horizontalCenterCandidates,
        compLayout.y + compLayout.height / 2,
      );
    }
    return filterHorizontalCandidates(equalDistanceCandidates, compLayout);
  }
  const verticalCenterCandidates = filterCenterCandidates(
    compLayout,
    equalDistanceCandidates,
    'x',
    'width',
  );
  if (verticalCenterCandidates.length > 0) {
    return addAxisAlignmentCoordinate(
      verticalCenterCandidates,
      compLayout.x + compLayout.width / 2,
    );
  }
  return filterVerticalCandidates(equalDistanceCandidates, compLayout);
};

function filterEqualDistanceCandidates(
  candidates: SnapCandidate[],
  currentPageId: string,
) {
  candidates = candidates.filter((pointer) => pointer.comp !== undefined);
  return candidates.filter(
    (pointer) =>
      pointer.comp.id !== 'SITE_HEADER' &&
      pointer.comp.id !== 'SITE_FOOTER' &&
      pointer.comp.id !== currentPageId,
  );
}

function filterCenterCandidates(
  compLayout: CompLayout,
  candidatesPointersAndLayouts: SnapCandidate[],
  axis: Axis,
  dimension: 'height' | 'width',
) {
  return candidatesPointersAndLayouts.filter((candidatePointerAndLayout) => {
    const centerXValue = compLayout[axis] + compLayout[dimension] / 2;
    const candidateLayout = candidatePointerAndLayout.layout;
    return (
      centerXValue === candidateLayout[axis] + candidateLayout[dimension] / 2
    );
  });
}

function filterCandidatesByCompBoundry(
  candidatesPointersAndLayouts: SnapCandidate[],
  compBound: number,
  axis: Axis,
) {
  const dimension = axis === 'x' ? 'width' : 'height';
  const compBoundryCandidates = candidatesPointersAndLayouts.filter(
    (candidatePointerAndLayout) => {
      const candidateLayout = candidatePointerAndLayout.layout;
      return (
        compBound === candidateLayout[axis] ||
        compBound === candidateLayout[axis] + candidateLayout[dimension]
      );
    },
  );
  return compBoundryCandidates.length > 0
    ? addAxisAlignmentCoordinate(compBoundryCandidates, compBound)
    : compBoundryCandidates;
}

function filterHorizontalCandidates(
  candidatesPointersAndLayouts: SnapCandidate[],
  compLayout: CompLayout,
) {
  const compBottom = compLayout.y + compLayout.height;
  const compTop = compLayout.y;
  const topCandidates = filterCandidatesByCompBoundry(
    candidatesPointersAndLayouts,
    compTop,
    'y',
  );
  const bottomCandidates = filterCandidatesByCompBoundry(
    candidatesPointersAndLayouts,
    compBottom,
    'y',
  );

  return topCandidates.concat(bottomCandidates);
}

function filterVerticalCandidates(
  candidatesPointersAndLayouts: SnapCandidate[],
  compLayout: CompLayout,
) {
  const compRight = compLayout.x + compLayout.width;
  const compLeft = compLayout.x;
  const leftCandidates = filterCandidatesByCompBoundry(
    candidatesPointersAndLayouts,
    compLeft,
    'x',
  );
  const rightCandidates = filterCandidatesByCompBoundry(
    candidatesPointersAndLayouts,
    compRight,
    'x',
  );

  return leftCandidates.concat(rightCandidates);
}

function addAxisAlignmentCoordinate(
  candidates: SnapCandidate[],
  axisValue: number,
) {
  return candidates.map((candidate) => ({ ...candidate, axisValue }));
}

function isLayoutVisible(stageRect: StageRect, compData: CompData) {
  const { layout } = compData;
  return (
    stageRect.right >= layout.x &&
    layout.x + layout.width >= stageRect.left && // intersection x
    stageRect.bottom >= layout.y &&
    layout.y + layout.height >= stageRect.top // intersection y
  );
}

const isContainerWidthCandidate = (candidate: SnapCandidate) =>
  candidate?.type === SnapCandidateType.ContainerWidthPercentage;

const isStageCenterCandidate = (candidate: SnapCandidate) =>
  candidate?.type === SnapCandidateType.StageCenter;

const isMarginIndicatorCandidate = (candidate: SnapCandidate) =>
  candidate?.type === SnapCandidateType.MarginIndicator;

export {
  isContainerWidthCandidate,
  isStageCenterCandidate,
  isMarginIndicatorCandidate,
  isLayoutVisible,
  getEqualDistanceCandidates,
};
